import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { ContractDetailModal } from '../components/ContractDetailModal';
import { ContractFormModal } from '../components/ContractFormModal';
import { ContractAiImportModal } from '../components/ContractAiImportModal';
import { SearchableDropdown } from '../components/SearchableDropdown';
import type { UploadedAttachment } from '../lib/api/attachments';
import { useAuth } from '../context/AuthContext';
import {
  contractsApi,
  type AgingRow,
  type ClassificationUnit,
  type Contract,
  type ContractKind,
  type ContractsSummary,
  type PayHealth,
  type RiskLevel,
  type RiskRow,
} from '../lib/api/contracts';
import { exportSheet, fmtDateISO } from '../lib/export-generic';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';

const PAGE_SIZE = 10;
type ContractFilters = { status: Contract['status'] | ''; payStatus: PayHealth | ''; businessUnit: string; serviceType: string };
const EMPTY_FILTERS: ContractFilters = { status: '', payStatus: '', businessUnit: '', serviceType: '' };

// Màu avatar customer theo hash tên (stable)
const AVATAR_COLORS = ['#2563EB', '#dc2626', '#16a34a', '#f59e0b', '#7c3aed', '#0891b2', '#94a3b8', '#ea580c'];
function avatarColor(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const STATUS_META: Record<Contract['status'], { label: string; bg: string; fg: string; dot: string }> = {
  active: { label: 'Đang thực hiện', bg: '#dcfce7', fg: '#15803d', dot: '#22c55e' },
  completed: { label: 'Hoàn tất', bg: '#e0e7ff', fg: '#3730a3', dot: '#6366f1' },
  paused: { label: 'Tạm dừng', bg: '#f1f5f9', fg: '#475569', dot: '#94a3b8' },
  cancelled: { label: 'Đã huỷ', bg: '#fee2e2', fg: '#b91c1c', dot: '#dc2626' },
};

const PAY_HEALTH_META: Record<PayHealth, { label: string; bg: string; fg: string; icon?: 'check' | 'alert' | 'clock' | null }> = {
  on_time: { label: 'Đúng hạn', bg: '#dcfce7', fg: '#15803d', icon: null },
  overdue: { label: 'Quá hạn', bg: '#fee2e2', fg: '#b91c1c', icon: 'alert' },
  due_soon: { label: 'Sắp đến hạn', bg: '#fef3c7', fg: '#a16207', icon: 'clock' },
  settled: { label: 'Đã tất toán', bg: '#e0e7ff', fg: '#3730a3', icon: 'check' },
};

const RISK_META: Record<RiskLevel, { label: string; bg: string; fg: string; borderColor: string }> = {
  high: { label: 'RỦI RO CAO', bg: '#fee2e2', fg: '#b91c1c', borderColor: '#fca5a5' },
  medium: { label: 'RỦI RO TB', bg: '#fef3c7', fg: '#a16207', borderColor: '#fcd34d' },
  low: { label: 'ON-TIME', bg: '#dcfce7', fg: '#15803d', borderColor: '#bbf7d0' },
};

export function ContractsPage() {
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const canWrite = user?.role === 'KTT' || user?.role === 'NV_KT';

  const [kind, setKind] = useState<ContractKind>('revenue');
  const [allContracts, setAllContracts] = useState<Contract[]>([]);
  const [summary, setSummary] = useState<ContractsSummary | null>(null);
  const [aging, setAging] = useState<AgingRow[]>([]);
  const [risk, setRisk] = useState<RiskRow[]>([]);
  const [classification, setClassification] = useState<ClassificationUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [aiImportOpen, setAiImportOpen] = useState(false);
  const [aiAttachment, setAiAttachment] = useState<UploadedAttachment | null>(null);
  const [aiSuggestedName, setAiSuggestedName] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<ContractFilters>(EMPTY_FILTERS);
  const [filterDraft, setFilterDraft] = useState<ContractFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isExpense = kind === 'expense';
  const partyLabel = isExpense ? 'Nhà cung cấp' : 'Khách hàng';
  const pageTitle = isExpense ? 'Hợp đồng nhà cung cấp' : 'Hợp đồng khách hàng';
  const pageSubtitle = isExpense
    ? 'Theo dõi HĐ chi · lịch chi trả · rủi ro thanh toán cho NCC'
    : 'Theo dõi giá trị · kỳ thanh toán · rủi ro thu hồi';

  const contracts = useMemo(() => allContracts.filter((c) => c.kind === kind), [allContracts, kind]);
  const tabCounts = useMemo(
    () => ({
      revenue: allContracts.filter((c) => c.kind === 'revenue').length,
      expense: allContracts.filter((c) => c.kind === 'expense').length,
    }),
    [allContracts],
  );

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [l, s, a, r, cls] = await Promise.all([
        contractsApi.list(),
        contractsApi.summary(kind),
        contractsApi.aging(kind),
        contractsApi.risk(kind),
        contractsApi.classification(kind),
      ]);
      setAllContracts(l.items); setSummary(s); setAging(a.items);
      setRisk(r.items); setClassification(cls.items);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSearch(''); }, [kind]);

  useEffect(() => {
    if (!menuOpenFor) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpenFor(null);
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [menuOpenFor]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contracts.filter((c) => {
      const matchesSearch = !q || c.code.toLowerCase().includes(q) || c.customer.toLowerCase().includes(q) || c.name.toLowerCase().includes(q);
      return matchesSearch
        && (!filters.status || c.status === filters.status)
        && (!filters.payStatus || c.payStatus === filters.payStatus)
        && (!filters.businessUnit || c.businessUnit === filters.businessUnit)
        && (!filters.serviceType || c.serviceType === filters.serviceType);
    });
  }, [contracts, search, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [search, filters]);

  const businessUnitOptions = [{ value: '', label: 'Tất cả đơn vị' }, ...Array.from(new Set(contracts.map((item) => item.businessUnit).filter((item): item is string => Boolean(item)))).sort().map((value) => ({ value, label: value }))];
  const serviceTypeOptions = [{ value: '', label: 'Tất cả dịch vụ' }, ...Array.from(new Set(contracts.map((item) => item.serviceType).filter((item): item is string => Boolean(item)))).sort().map((value) => ({ value, label: value }))];
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const agingTotal = aging.reduce((s, r) => s + r.total, 0);

  const handleDelete = async (c: Contract) => {
    setMenuOpenFor(null);
    if (!window.confirm(`Xoá HĐ ${c.code} - ${c.name}?`)) return;
    try {
      await contractsApi.remove(c.id);
      showToast(`Đã xoá ${c.code}`);
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <AppShell title={pageTitle} subtitle={pageSubtitle}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      {/* Tab: Thu / Chi — style đồng nhất với Trung tâm phê duyệt */}
      <div className="apr-tabs">
        <TabButton active={kind === 'revenue'} onClick={() => setKind('revenue')} label="Hợp đồng thu (KH)" count={tabCounts.revenue} />
        <TabButton active={kind === 'expense'} onClick={() => setKind('expense')} label="Hợp đồng chi (NCC)" count={tabCounts.expense} />
      </div>

      {/* KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <KpiCard gradient="linear-gradient(to right,#2563EB,#3b82f6)" label="HĐ đang thực hiện" value={summary?.activeCount ?? '—'} unit="hợp đồng" fg="#172033" labelFg="#667085" />
        <KpiCard gradient="linear-gradient(to right,#16a34a,#22c55e)" label="Tổng giá trị active" value={summary ? formatVND(summary.totalActiveValue) : '—'} fg="#15803d" labelFg="#15803d" />
        <KpiCard gradient="linear-gradient(to right,#f59e0b,#fbbf24)" label="Sắp đến hạn (7 ngày)" value={summary?.dueSoonCount ?? '—'} unit="kỳ" fg="#a16207" labelFg="#a16207" />
        <KpiCard
          gradient="linear-gradient(to right,#dc2626,#ef4444)"
          label={<span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /></svg>Kỳ QUÁ HẠN</span>}
          value={summary?.overdueCount ?? '—'}
          unit={summary ? `kỳ · ${summary.overdueCustomers} ${isExpense ? 'NCC' : 'KH'}` : ''}
          fg="#b91c1c"
          labelFg="#b91c1c"
          cardBg="linear-gradient(135deg,#fee2e2,#fecaca)"
          borderColor="#fca5a5"
        />
      </div>

      {/* ALERT quá hạn */}
      {summary && summary.overdueList.length > 0 && (
        <div style={{ background: 'linear-gradient(135deg,#fef2f2 0%,#fee2e2 100%)', border: '1px solid #fca5a5', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 9999, background: '#dc2626', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(220,38,38,.25)' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4M12 17h.01" /></svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#7f1d1d' }}>Có {summary.overdueList.length} kỳ {isExpense ? 'chi trả' : 'thanh toán'} QUÁ HẠN cần xử lý</div>
            <div style={{ fontSize: 12, color: '#991b1b', marginTop: 2, lineHeight: 1.5 }}>
              {summary.overdueList.slice(0, 4).map((o, i) => (
                <span key={i}>
                  {i > 0 && <span style={{ margin: '0 8px', color: '#c53030' }}>|</span>}
                  <b>{o.customer}</b> ({o.code}) — kỳ {o.periodIndex} quá hạn <b>{o.overdueDays} ngày</b> · {formatVND(o.amount)}
                </span>
              ))}
              {summary.overdueList.length > 4 && <span style={{ marginLeft: 8, color: '#7f1d1d' }}>… và {summary.overdueList.length - 4} kỳ khác</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={() => showToast(isExpense ? 'Nhắc chi trả NCC — đang phát triển' : 'Gửi nhắc thanh toán — đang phát triển')}
            style={{ padding: '8px 14px', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
            {isExpense ? 'Nhắc chi trả NCC' : 'Gửi nhắc thanh toán'}
          </button>
        </div>
      )}

      {/* AI risk analysis */}
      {risk.length > 0 && (
        <div style={{ background: 'linear-gradient(135deg,#eff6ff 0%,#f5f3ff 100%)', border: '1px solid #bfdbfe', borderRadius: 12, padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{ width: 28, height: 28, background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" /></svg>
            </div>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#1e3a8a' }}>AI dự báo rủi ro {isExpense ? 'chi trả' : 'thanh toán'}</h3>
            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#1d4ed8', fontWeight: 600 }}>Phân tích lịch sử payments · Real-time</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {risk.slice(0, 3).map((r) => {
              const meta = RISK_META[r.level];
              return (
                <div
                  key={r.contractId}
                  onClick={() => setDetailId(r.contractId)}
                  style={{ background: '#fff', border: `1px solid ${meta.borderColor}`, borderRadius: 8, padding: '12px 14px', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ padding: '1px 8px', background: meta.bg, color: meta.fg, fontSize: 10, fontWeight: 700, borderRadius: 9999 }}>
                      {meta.label} {r.level !== 'low' && `${100 - r.onTimeRate}%`}
                      {r.level === 'low' && `${r.onTimeRate}%`}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: meta.fg, marginBottom: 3 }}>{r.customer} · {r.contractCode}</div>
                  <div style={{ fontSize: 11, color: '#334155', lineHeight: 1.5 }}>{r.hint}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main workspace — cùng section với toolbar và bảng như Trung tâm thanh toán */}
      <div className="pay-page-card ctr-workspace">
        <div className="pay-toolbar">
          <div className="pay-toolbar-left">
            <div className="pay-search" style={{ minWidth: 280 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
              <input placeholder={`Tìm mã HĐ, tên ${isExpense ? 'nhà cung cấp' : 'khách hàng'}…`} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <button type="button" className="pay-btn-outline" onClick={() => { setFilterDraft(filters); setFilterOpen(true); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="21" x2="14" y1="4" y2="4" /><line x1="10" x2="3" y1="4" y2="4" />
                <line x1="21" x2="12" y1="12" y2="12" /><line x1="8" x2="3" y1="12" y2="12" />
                <line x1="21" x2="16" y1="20" y2="20" /><line x1="12" x2="3" y1="20" y2="20" />
                <line x1="14" x2="14" y1="2" y2="6" /><line x1="8" x2="8" y1="10" y2="14" /><line x1="16" x2="16" y1="18" y2="22" />
              </svg>
              Bộ lọc
              {activeFilterCount > 0 && <span className="apr-filter-badge">{activeFilterCount}</span>}
            </button>
          </div>
          <div className="pay-toolbar-right">
            <button
              type="button"
              onClick={() => {
                exportSheet('contracts', filtered, [
                  { header: 'Mã HĐ', key: 'code', width: 22 },
                  { header: partyLabel, key: 'customer', width: 25 },
                  { header: 'Tên HĐ', key: 'name', width: 40 },
                  { header: 'Business Unit', key: 'businessUnit', width: 15 },
                  { header: 'Loại dịch vụ', key: 'serviceType', width: 20 },
                  { header: 'Giá trị', key: 'value', width: 18, format: (v) => Number(v) },
                  { header: 'Ngày ký', key: 'signedAt', width: 12, format: (v) => fmtDateISO(v) },
                  { header: 'Kỳ đã TT', key: 'periodsPaid', width: 8 },
                  { header: 'Tổng kỳ', key: 'periodsTotal', width: 8 },
                  { header: 'Trạng thái', key: 'status', width: 14 },
                ], 'Hợp đồng');
                showToast(`Đã xuất ${filtered.length} hợp đồng`);
              }}
              className="pay-btn-outline"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
              Xuất Excel
            </button>
            {canWrite && (
              <button type="button" className="pay-btn-outline" onClick={() => setAiImportOpen(true)}>
                <span style={{ color: '#2563eb', fontWeight: 800 }}>✦</span> AI đọc HĐ
              </button>
            )}
            {canWrite && (
              <button type="button" className="pay-btn-primary" onClick={() => { setAiAttachment(null); setAiSuggestedName(undefined); setFormOpen(true); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M5 12h14M12 5v14" /></svg>
                Thêm HĐ {isExpense ? 'chi' : 'thu'}
              </button>
            )}
          </div>
        </div>

        <div className="pay-table-wrap">
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Đang tải…</div>
          ) : paged.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              {search ? `Không có HĐ khớp "${search}"` : 'Chưa có HĐ nào'}
            </div>
          ) : (
            <table className="pay-tbl ctr-table" style={{ minWidth: 1080 }}>
              <thead>
                <tr>
                  <th style={{ padding: '0 14px', fontWeight: 600, width: 150 }}>Mã HĐ</th>
                  <th style={{ padding: '0 14px', fontWeight: 600, minWidth: 260 }}>{partyLabel} · Tên HĐ</th>
                  <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'right', width: 140 }}>Giá trị</th>
                  <th style={{ padding: '0 14px', fontWeight: 600, width: 110 }}>Ngày ký</th>
                  <th style={{ padding: '0 14px', fontWeight: 600, width: 180 }}>Kỳ đã {isExpense ? 'chi trả' : 'thanh toán'}</th>
                  <th style={{ padding: '0 14px', fontWeight: 600, width: 140 }}>Trạng thái HĐ</th>
                  <th style={{ padding: '0 14px', fontWeight: 600, width: 130 }}>Thanh toán</th>
                  <th style={{ padding: '0 14px', fontWeight: 600, width: 60, textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {paged.map((c) => {
                  const st = STATUS_META[c.status];
                  const ph = PAY_HEALTH_META[c.payStatus ?? 'on_time'];
                  const pct = c.periodsTotal > 0 ? Math.round((c.periodsPaid / c.periodsTotal) * 100) : 0;
                  return (
                    <tr
                      key={c.id}
                      onClick={() => setDetailId(c.id)}
                      className="pay-row"
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ padding: '0 14px', fontSize: 12, fontWeight: 700, color: '#172033' }}>{c.code}</td>
                      <td style={{ padding: '0 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: avatarColor(c.customer), color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {initials(c.customer)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#172033' }}>{c.customer}</div>
                            <div style={{ fontSize: 11, color: '#667085', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 250 }} title={c.name}>{c.name}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '0 14px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#172033' }}>{formatVND(c.value)}</td>
                      <td style={{ padding: '0 14px', fontSize: 12, color: '#667085' }}>{new Date(c.signedAt).toLocaleDateString('vi-VN')}</td>
                      <td style={{ padding: '0 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ fontSize: 12, color: '#334155', fontWeight: 600, whiteSpace: 'nowrap' }}>{c.periodsPaid}/{c.periodsTotal} kỳ</div>
                          <div style={{ flex: 1, minWidth: 60, height: 6, background: '#f1f5f9', borderRadius: 9999, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: '#22c55e' }} />
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '0 14px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: st.bg, color: st.fg, fontSize: 11, fontWeight: 600, borderRadius: 9999 }}>
                          <span style={{ width: 6, height: 6, background: st.dot, borderRadius: 9999, display: 'inline-block' }} />
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: '0 14px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', background: ph.bg, color: ph.fg, fontSize: 11, fontWeight: 600, borderRadius: 9999 }}>
                          <PayHealthIcon icon={ph.icon ?? null} />
                          {ph.label}
                        </span>
                      </td>
                      <td style={{ padding: '0 14px', textAlign: 'right', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => setMenuOpenFor(menuOpenFor === c.id ? null : c.id)}
                          title="Actions"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', width: 28, height: 28, borderRadius: 6 }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></svg>
                        </button>
                        {menuOpenFor === c.id && (
                          <div ref={menuRef} style={kebabMenu}>
                            <button type="button" style={kebabItem} onClick={() => { setMenuOpenFor(null); setDetailId(c.id); }}>Xem chi tiết</button>
                            {canWrite && (
                              <button type="button" style={kebabItem} onClick={() => { setMenuOpenFor(null); setEditingId(c.id); }}>Sửa</button>
                            )}
                            {user?.role === 'KTT' && (
                              <button type="button" style={{ ...kebabItem, color: '#b91c1c' }} onClick={() => handleDelete(c)}>Xoá</button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {filtered.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, padding: '0 4px' }}>
            <div style={{ color: '#667085', fontSize: 13 }}>
              Hiển thị {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} của {filtered.length} hợp đồng
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button type="button" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={pageBtn(false, currentPage <= 1)}>◀ Trước</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button key={n} type="button" onClick={() => setPage(n)} style={pageBtn(n === currentPage, false)}>{n}</button>
              ))}
              <button type="button" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={pageBtn(false, currentPage >= totalPages)}>Sau ▶</button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom: aging + classification */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 16 }}>
        <div className="pay-page-card ctr-secondary-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#172033' }}>Phân tích tuổi {isExpense ? 'công nợ phải trả' : 'nợ'} (Aging)</h3>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#667085' }}>
                {isExpense ? 'Công nợ phải trả NCC' : 'Công nợ'} theo mức độ trễ · Tổng {formatVND(agingTotal)}
              </p>
            </div>
          </div>
          <div className="pay-table-wrap">
          <table className="pay-tbl ctr-aging-table" style={{ minWidth: 720 }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr style={{ textTransform: 'uppercase', color: '#64748b', fontSize: 11, letterSpacing: 0.4, borderBottom: '1px solid #eef2f7', height: 38, textAlign: 'left' }}>
                <th style={{ padding: '0 14px', fontWeight: 600 }}>{partyLabel}</th>
                <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'right' }}>Chưa đến hạn</th>
                <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'right' }}>0-30 ngày</th>
                <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'right' }}>31-60 ngày</th>
                <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'right' }}>61-90 ngày</th>
                <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'right', background: '#fef2f2', color: '#b91c1c' }}>&gt;90 ngày</th>
                <th style={{ padding: '0 14px', fontWeight: 600, textAlign: 'right' }}>Tổng</th>
              </tr>
            </thead>
            <tbody>
              {aging.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Không có {isExpense ? 'công nợ phải trả' : 'công nợ'} nào.</td></tr>
              ) : aging.map((r) => (
                <tr key={r.customer} style={{ borderTop: '1px solid #f1f5f9', height: 40 }}>
                  <td style={{ padding: '0 14px', color: '#172033', fontWeight: 500 }}>{r.customer}</td>
                  <AmountCell v={r.notDue} />
                  <AmountCell v={r.d30} tone="amber" />
                  <AmountCell v={r.d60} tone="amber" />
                  <AmountCell v={r.d90} tone="red" />
                  <AmountCell v={r.over90} tone="danger" />
                  <td style={{ padding: '0 14px', textAlign: 'right', color: '#172033', fontWeight: 700 }}>{formatVND(r.total)}</td>
                </tr>
              ))}
              {aging.length > 0 && (
                <tr style={{ background: '#f8fafc', fontWeight: 700, borderTop: '2px solid #e4e7ec', height: 44 }}>
                  <td style={{ padding: '0 14px', color: '#172033' }}>Tổng cộng · {aging.length} {isExpense ? 'NCC' : 'KH'}</td>
                  <td style={{ padding: '0 14px', textAlign: 'right' }}>{formatVND(aging.reduce((s, r) => s + r.notDue, 0))}</td>
                  <td style={{ padding: '0 14px', textAlign: 'right' }}>{formatVND(aging.reduce((s, r) => s + r.d30, 0))}</td>
                  <td style={{ padding: '0 14px', textAlign: 'right' }}>{formatVND(aging.reduce((s, r) => s + r.d60, 0))}</td>
                  <td style={{ padding: '0 14px', textAlign: 'right' }}>{formatVND(aging.reduce((s, r) => s + r.d90, 0))}</td>
                  <td style={{ padding: '0 14px', textAlign: 'right', background: '#fef2f2', color: '#b91c1c' }}>{formatVND(aging.reduce((s, r) => s + r.over90, 0))}</td>
                  <td style={{ padding: '0 14px', textAlign: 'right', color: '#172033' }}>{formatVND(agingTotal)}</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* Classification */}
        <div className="pay-page-card ctr-secondary-card">
          <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#172033' }}>Phân loại hợp đồng</h3>
          <p style={{ margin: '0 0 14px', fontSize: 11, color: '#667085' }}>Theo business unit + loại dịch vụ (HĐ active)</p>

          {classification.length === 0 ? (
            <div style={{ padding: 12, color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
              Chưa có HĐ active để phân loại.
            </div>
          ) : (
            <>
              {classification.map((u, i) => (
                <UnitBlock key={u.unit} unit={u} color={UNIT_COLORS[i % UNIT_COLORS.length]} />
              ))}
              <div style={{ marginTop: 14, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 11, color: '#1e3a8a', lineHeight: 1.5 }}>
                <b>Ghi chú:</b> Chỉ tính HĐ đang <b>active</b>. Đặt Business Unit + Loại dịch vụ khi tạo/sửa HĐ để phân loại chuẩn hơn.
              </div>
            </>
          )}
        </div>
      </div>
      </div>

      <ContractFormModal
        open={formOpen || editingId !== null}
        editingId={editingId}
        defaultKind={kind}
        initialAttachment={aiAttachment}
        initialName={aiSuggestedName}
        onClose={() => { setFormOpen(false); setEditingId(null); }}
        onSaved={() => {
          const wasEdit = editingId !== null;
          setFormOpen(false); setEditingId(null);
          showToast(wasEdit ? 'Đã cập nhật hợp đồng' : `Đã tạo HĐ ${isExpense ? 'chi' : 'thu'}`);
          load();
        }}
      />

      <ContractAiImportModal
        open={aiImportOpen}
        onClose={() => setAiImportOpen(false)}
        onReady={(file, suggestedName) => {
          setAiImportOpen(false);
          setAiAttachment(file);
          setAiSuggestedName(suggestedName);
          setFormOpen(true);
          showToast('Đã upload tài liệu. Kiểm tra và hoàn tất thông tin hợp đồng.');
        }}
      />
      {filterOpen && (
        <div className="cvm-backdrop" onClick={() => setFilterOpen(false)}>
          <section className="ctf2-modal payroll-filter-modal" style={{ maxWidth: 580 }} onClick={(event) => event.stopPropagation()} aria-modal="true" role="dialog" aria-label="Bộ lọc hợp đồng">
            <header className="ctf2-head">
              <div className="ctf2-head-left"><div className="ctf2-head-icon" aria-hidden="true">☷</div><div><h3>Bộ lọc hợp đồng</h3><p>Lọc danh sách theo trạng thái, đơn vị và loại dịch vụ.</p></div></div>
              <button className="ctf2-close" type="button" onClick={() => setFilterOpen(false)} aria-label="Đóng">✕</button>
            </header>
            <div className="ctf2-body"><div className="ctf2-card"><div className="ctf2-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <div className="ctf2-field"><label>Trạng thái HĐ</label><SearchableDropdown ariaLabel="Trạng thái hợp đồng" value={filterDraft.status} options={[{ value: '', label: 'Tất cả trạng thái' }, ...Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label }))]} onChange={(value) => setFilterDraft((current) => ({ ...current, status: value as ContractFilters['status'] }))} /></div>
              <div className="ctf2-field"><label>Thanh toán</label><SearchableDropdown ariaLabel="Tình trạng thanh toán" value={filterDraft.payStatus} options={[{ value: '', label: 'Tất cả tình trạng' }, ...Object.entries(PAY_HEALTH_META).map(([value, meta]) => ({ value, label: meta.label }))]} onChange={(value) => setFilterDraft((current) => ({ ...current, payStatus: value as ContractFilters['payStatus'] }))} /></div>
              <div className="ctf2-field"><label>Đơn vị kinh doanh</label><SearchableDropdown ariaLabel="Đơn vị kinh doanh" value={filterDraft.businessUnit} options={businessUnitOptions} onChange={(value) => setFilterDraft((current) => ({ ...current, businessUnit: value }))} /></div>
              <div className="ctf2-field"><label>Loại dịch vụ</label><SearchableDropdown ariaLabel="Loại dịch vụ" value={filterDraft.serviceType} options={serviceTypeOptions} onChange={(value) => setFilterDraft((current) => ({ ...current, serviceType: value }))} /></div>
            </div></div></div>
            <footer className="ctf2-foot" style={{ justifyContent: 'space-between' }}><button type="button" className="ctf2-btn-cancel" onClick={() => setFilterDraft(EMPTY_FILTERS)}>Đặt lại</button><div style={{ display: 'flex', gap: 8 }}><button type="button" className="ctf2-btn-cancel" onClick={() => setFilterOpen(false)}>Huỷ</button><button type="button" className="pay-btn-primary" onClick={() => { setFilters(filterDraft); setFilterOpen(false); }}>Áp dụng</button></div></footer>
          </section>
        </div>
      )}

      <ContractDetailModal
        open={detailId !== null}
        contractId={detailId}
        onClose={() => setDetailId(null)}
        onEdit={(id) => { setDetailId(null); setEditingId(id); }}
        onChanged={load}
        canWrite={canWrite}
        canDelete={user?.role === 'KTT'}
      />

      {toast && <div className="coa-toast">{toast}</div>}
    </AppShell>
  );
}

// ==== Helper components ====

function TabButton({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button type="button" className={`apr-tab ${active ? 'active' : ''}`} onClick={onClick}>
      {label}
      <span className="apr-tab-badge">{count}</span>
    </button>
  );
}

function KpiCard({ gradient, label, value, unit, fg, labelFg, cardBg, borderColor }: {
  gradient: string; label: React.ReactNode; value: string | number; unit?: string;
  fg: string; labelFg: string; cardBg?: string; borderColor?: string;
}) {
  return (
    <div style={{ background: cardBg ?? '#fff', border: `1px solid ${borderColor ?? '#e4e7ec'}`, borderRadius: 12, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: gradient }} />
      <div style={{ fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: 0.4, color: labelFg, fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: fg }}>
        {value}
        {unit && <span style={{ fontSize: 12, color: fg === '#b91c1c' ? '#b91c1c' : '#94a3b8', fontWeight: 500, marginLeft: 6 }}>{unit}</span>}
      </div>
    </div>
  );
}

function PayHealthIcon({ icon }: { icon: 'check' | 'alert' | 'clock' | null }) {
  if (!icon) return null;
  if (icon === 'check') return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>;
  if (icon === 'alert') return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /></svg>;
  return <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></svg>;
}

function AmountCell({ v, tone = 'plain' }: { v: number; tone?: 'plain' | 'amber' | 'red' | 'danger' }) {
  const colors: Record<string, { color: string; bg?: string; weight?: number }> = {
    plain: { color: v > 0 ? '#334155' : '#cbd5e1' },
    amber: { color: v > 0 ? '#a16207' : '#cbd5e1', weight: v > 0 ? 600 : undefined },
    red: { color: v > 0 ? '#b91c1c' : '#cbd5e1', weight: v > 0 ? 600 : undefined },
    danger: { color: v > 0 ? '#b91c1c' : '#cbd5e1', bg: '#fef2f2', weight: v > 0 ? 700 : undefined },
  };
  const c = colors[tone];
  return (
    <td style={{ padding: '0 14px', textAlign: 'right', color: c.color, fontWeight: c.weight, background: c.bg }}>
      {v > 0 ? formatVND(v) : '—'}
    </td>
  );
}

const UNIT_COLORS = ['#2563EB', '#7c3aed', '#16a34a', '#f59e0b', '#dc2626', '#0891b2'];

function UnitBlock({ unit, color }: { unit: ClassificationUnit; color: string }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, background: color, borderRadius: 2 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#172033' }}>{unit.unit}</span>
          <span style={{ fontSize: 10, color: '#94a3b8' }}>({unit.count} HĐ · {formatVND(unit.total)})</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 14 }}>
        {unit.services.map((s) => (
          <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
            <span style={{ color: '#334155' }}>{s.name}</span>
            <span style={{ color: '#64748b' }}>{s.count} HĐ · {formatVND(s.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==== Inline styles ====

const toolbarBtnLight: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  height: 38, padding: '0 14px', background: '#fff', color: '#334155',
  fontSize: 13, fontWeight: 500, border: '1px solid #e4e7ec', borderRadius: 8, cursor: 'pointer',
};

const toolbarBtnDark: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  height: 38, padding: '0 14px', background: '#18212f', color: '#fff',
  fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, cursor: 'pointer',
};

const kebabMenu: React.CSSProperties = {
  position: 'absolute', top: '100%', right: 8, minWidth: 160,
  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)', padding: 4, zIndex: 20, textAlign: 'left',
};

const kebabItem: React.CSSProperties = {
  display: 'block', width: '100%', padding: '8px 12px',
  background: 'transparent', border: 'none', cursor: 'pointer',
  fontSize: 13, color: '#334155', textAlign: 'left', borderRadius: 4,
};

function pageBtn(active: boolean, disabled: boolean): React.CSSProperties {
  return {
    padding: '6px 12px', background: active ? '#eff6ff' : '#fff',
    border: `1px solid ${active ? '#2563EB' : '#e4e7ec'}`,
    color: active ? '#2563EB' : disabled ? '#94a3b8' : '#334155',
    fontSize: 13, fontWeight: active ? 600 : 500, borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
